import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent } from '../../components/ui/card';
import { Shield, FileCheck, Users, Lock, Eye, Trash2, Download, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';

const GDPRCompliance = () => {
  const lastUpdated = "20 January 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-[#42b1c2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-[#42b1c2]" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">GDPR Compliance</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>

          {/* Important Notice */}
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> This page describes how Luma Med Global aligns with the principles of the 
                    General Data Protection Regulation (GDPR). This information is provided for transparency purposes and 
                    does not constitute legal advice or formal certification of GDPR compliance. For specific legal guidance, 
                    please consult a qualified data protection professional.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            
            {/* Introduction */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FileCheck className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Our Commitment to Data Protection
                </h2>
                <p className="text-gray-600 mb-4">
                  Luma Med Global is committed to protecting the personal data of our users in accordance with the 
                  General Data Protection Regulation (GDPR) and other applicable data protection laws. As a medically 
                  led recruitment platform, we understand the sensitivity of the information entrusted to us and take 
                  our data protection responsibilities seriously.
                </p>
                <p className="text-gray-600">
                  This page outlines how we apply GDPR principles to our data processing activities and explains 
                  your rights as a data subject.
                </p>
              </CardContent>
            </Card>

            {/* GDPR Principles */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">GDPR Principles We Follow</h2>
                <p className="text-gray-600 mb-4">
                  Our data processing activities are guided by the core principles established in the GDPR:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#42b1c2]">
                    <h3 className="font-medium text-gray-900 mb-2">1. Lawfulness, Fairness, and Transparency</h3>
                    <p className="text-sm text-gray-600">
                      We process personal data lawfully, fairly, and in a transparent manner. We clearly inform users 
                      about how their data is collected, used, and shared through our Privacy Policy and this compliance page.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#42b1c2]">
                    <h3 className="font-medium text-gray-900 mb-2">2. Purpose Limitation</h3>
                    <p className="text-sm text-gray-600">
                      We collect data for specified, explicit, and legitimate purposes—primarily recruitment facilitation, 
                      credential verification, and platform improvement. We do not use personal data for purposes 
                      incompatible with these stated objectives.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#42b1c2]">
                    <h3 className="font-medium text-gray-900 mb-2">3. Data Minimisation</h3>
                    <p className="text-sm text-gray-600">
                      We only collect personal data that is adequate, relevant, and limited to what is necessary for 
                      the purposes for which it is processed. We regularly review our data collection practices to 
                      ensure we are not collecting unnecessary information.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#42b1c2]">
                    <h3 className="font-medium text-gray-900 mb-2">4. Accuracy</h3>
                    <p className="text-sm text-gray-600">
                      We take reasonable steps to ensure personal data is accurate and kept up to date. Users can 
                      update their profile information at any time and request corrections to inaccurate data.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#42b1c2]">
                    <h3 className="font-medium text-gray-900 mb-2">5. Storage Limitation</h3>
                    <p className="text-sm text-gray-600">
                      We retain personal data only for as long as necessary to fulfil the purposes for which it was 
                      collected, or as required by law. Inactive accounts are archived after a reasonable period, 
                      and data is deleted upon valid request.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#42b1c2]">
                    <h3 className="font-medium text-gray-900 mb-2">6. Integrity and Confidentiality</h3>
                    <p className="text-sm text-gray-600">
                      We implement appropriate technical and organisational measures to ensure the security of 
                      personal data, protecting against unauthorised access, loss, destruction, or damage.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#42b1c2]">
                    <h3 className="font-medium text-gray-900 mb-2">7. Accountability</h3>
                    <p className="text-sm text-gray-600">
                      We take responsibility for our data processing activities and can demonstrate compliance 
                      with GDPR principles through documentation, policies, and procedures.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lawful Basis */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Lock className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Lawful Basis for Processing
                </h2>
                <p className="text-gray-600 mb-4">
                  Under GDPR, we must have a valid lawful basis to process personal data. We rely on the following 
                  legal bases for our processing activities:
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h3 className="font-medium text-emerald-800 mb-2 flex items-center">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Consent
                    </h3>
                    <p className="text-sm text-gray-600">
                      When you create an account and submit your profile, you provide explicit consent for us to 
                      process your data for recruitment purposes. You can withdraw consent at any time.
                    </p>
                  </div>
                  
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h3 className="font-medium text-emerald-800 mb-2 flex items-center">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Legitimate Interest
                    </h3>
                    <p className="text-sm text-gray-600">
                      We process certain data based on our legitimate interest in providing and improving our 
                      recruitment services, provided this does not override your rights and interests.
                    </p>
                  </div>
                  
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h3 className="font-medium text-emerald-800 mb-2 flex items-center">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Contractual Necessity
                    </h3>
                    <p className="text-sm text-gray-600">
                      Some processing is necessary to provide the services you have requested when using our 
                      platform (e.g., matching you with job opportunities).
                    </p>
                  </div>
                  
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h3 className="font-medium text-emerald-800 mb-2 flex items-center">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Legal Obligation
                    </h3>
                    <p className="text-sm text-gray-600">
                      In some cases, we are required by law to process or retain certain personal data 
                      (e.g., for tax or regulatory purposes).
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Your Rights Under GDPR
                </h2>
                <p className="text-gray-600 mb-4">
                  As a data subject, you have the following rights under GDPR. We are committed to facilitating 
                  the exercise of these rights:
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                    <Eye className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Right of Access</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        You have the right to request a copy of the personal data we hold about you. We will provide 
                        this information free of charge within one month of receiving your request.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                    <FileCheck className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Right to Rectification</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        You have the right to request correction of inaccurate personal data or completion of 
                        incomplete data. You can update most information directly through your account settings.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                    <Trash2 className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Right to Erasure ("Right to be Forgotten")</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        You have the right to request deletion of your personal data in certain circumstances, 
                        such as when the data is no longer necessary or you withdraw consent. We will comply 
                        within 30 days unless legal retention requirements apply.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                    <Lock className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Right to Restrict Processing</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        You have the right to request that we limit how we process your data in certain 
                        circumstances, such as while we verify the accuracy of contested data.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                    <Download className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Right to Data Portability</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        You have the right to receive your personal data in a structured, commonly used, and 
                        machine-readable format, and to have that data transmitted to another controller where 
                        technically feasible.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                    <Shield className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Right to Object</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        You have the right to object to processing based on legitimate interests or for direct 
                        marketing purposes. If you object to marketing, we will stop processing your data for 
                        that purpose immediately.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How to Exercise Rights */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">How to Exercise Your Rights</h2>
                <p className="text-gray-600 mb-4">
                  To exercise any of your data protection rights, you can:
                </p>
                <ul className="space-y-2 text-gray-600 mb-4">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Email us:</strong> Send your request to <span className="text-[#42b1c2]">info@lumamedglobal.com</span></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Use your account settings:</strong> Update or delete certain information directly through your profile</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Contact us in writing:</strong> Send correspondence to our registered address</span>
                  </li>
                </ul>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Verification:</strong> To protect your privacy, we may need to verify your identity before 
                    processing your request. We aim to respond to all valid requests within one month, though this 
                    may be extended by up to two months for complex requests.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* International Transfers */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">International Data Transfers</h2>
                <p className="text-gray-600 mb-4">
                  As a recruitment platform connecting healthcare professionals with opportunities in the Middle East, 
                  we may need to transfer personal data outside the European Economic Area (EEA). When we do so, we 
                  ensure appropriate safeguards are in place:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Using standard contractual clauses approved by the European Commission</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Transferring to countries with adequate data protection laws</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Obtaining your explicit consent for specific transfers where appropriate</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="mb-6 border-[#42b1c2]/30 bg-[#42b1c2]/5">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Mail className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Data Protection Contact
                </h2>
                <p className="text-gray-600 mb-4">
                  For any questions about our GDPR compliance or to submit a data protection request:
                </p>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Email:</strong> info@lumamedglobal.com</p>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  You also have the right to lodge a complaint with your local data protection authority if you 
                  believe your rights have been violated. In the UK, this is the Information Commissioner's Office (ICO).
                </p>
              </CardContent>
            </Card>

            {/* Updates */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Policy Updates</h2>
                <p className="text-gray-600">
                  We may update this GDPR compliance information from time to time to reflect changes in our practices 
                  or legal requirements. We will notify you of any significant changes and update the "Last updated" 
                  date at the top of this page.
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GDPRCompliance;
